import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PageWrapper } from '../../components';
import messageThreadsApiService from '../../services/messageThreadsApi';
import userProfileService from '../../services/userProfileService';
import {
  buildReadMapFromThreads,
  getThreadReadMap,
  hasThreadReadMap,
  isThreadUnreadForViewer,
  setThreadReadMap,
  syncThreadReadWithServer,
  threadListHasServerUnread,
} from '../../utils/messageReadTracker';
import { isMessageSentByViewer, isActorOrganizerForThread, normalizeIdentity } from '../../utils/messageOwnership';
import './Messages.css';

const formatThreadTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  return isToday
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString([], { day: '2-digit', month: 'short' });
};

const getUserId = (currentUser, userProfile) =>
  userProfile?.backend_user_id ||
  userProfile?.id ||
  userProfile?.firebase_uid ||
  currentUser?.uid ||
  null;

const isAdminUser = (userProfile) =>
  userProfile?.is_admin === true ||
  userProfile?.role === 'admin' ||
  userProfile?.user_type === 'admin';

const collectThreadIdentities = (thread) => {
  const identities = new Set();
  const add = (value) => {
    const normalized = normalizeIdentity(value);
    if (normalized) identities.add(normalized);
  };

  add(thread?.created_by_user_id);
  add(thread?.created_by);
  add(thread?.owner_id);
  add(thread?.user_id);
  add(thread?.organizer_id);
  add(thread?.event_organizer_id);
  add(thread?.customer_id);
  add(thread?.created_by_email);
  add(thread?.owner_email);
  add(thread?.organizer_email);
  add(thread?.customer_email);
  add(thread?.guest_organizer_email);
  add(thread?.guestOrganizerEmail);

  if (Array.isArray(thread?.participants)) {
    thread.participants.forEach((participant) => {
      add(participant?.user_id);
      add(participant?.id);
      add(participant?.email);
    });
  }

  if (Array.isArray(thread?.participant_user_ids)) {
    thread.participant_user_ids.forEach(add);
  }
  if (Array.isArray(thread?.participant_emails)) {
    thread.participant_emails.forEach(add);
  }

  return identities;
};

const getCounterpartyId = (messages, actorUserId) => {
  const actorId = normalizeIdentity(actorUserId);
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const senderId = normalizeIdentity(messages[i]?.sender_id);
    const receiverId = normalizeIdentity(messages[i]?.receiver_id);
    if (senderId && senderId !== actorId) return messages[i].sender_id;
    if (receiverId && receiverId !== actorId) return messages[i].receiver_id;
  }
  return null;
};

const Messages = () => {
  const { currentUser, userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedThreadId = searchParams.get('threadId');
  const urlBookingId = searchParams.get('bookingId');
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [composeText, setComposeText] = useState('');
  const [error, setError] = useState('');
  const [readMap, setReadMap] = useState({});
  const [adminThreadFilter, setAdminThreadFilter] = useState('all');
  const [threadBookingContacts, setThreadBookingContacts] = useState({});
  const chatBodyRef = useRef(null);
  const actorUserId = getUserId(currentUser, userProfile);
  const actorEmail = normalizeIdentity(currentUser?.email || userProfile?.email);
  const isAdmin = isAdminUser(userProfile);

  const enrichThreadWithBookingContact = useCallback((thread) => {
    if (!thread) return thread;
    const bookingId = thread.booking_id || thread.bookingId;
    const contact = bookingId ? threadBookingContacts[String(bookingId)] : null;
    if (!contact) return thread;
    return { ...thread, ...contact };
  }, [threadBookingContacts]);

  const classifiedThreads = useMemo(() => {
    if (!isAdmin) {
      return threads.map((thread) => ({ ...thread, __threadScope: 'mine' }));
    }
    return threads.map((thread) => {
      const enriched = enrichThreadWithBookingContact(thread);
      const bookingId = enriched.booking_id || enriched.bookingId;
      const guestEmail = normalizeIdentity(
        enriched.guest_organizer_email || enriched.guestOrganizerEmail
      );

      if (isActorOrganizerForThread(enriched, actorUserId, actorEmail)) {
        return { ...thread, __threadScope: 'mine' };
      }

      // Booking thread: only the guest organiser is "mine"; mis-linked created_by / customer_id
      // must not grant organiser access (e.g. venue admin who created a manual booking).
      if (bookingId) {
        return { ...thread, __threadScope: 'observed' };
      }

      if (guestEmail) {
        return { ...thread, __threadScope: 'observed' };
      }

      const identities = collectThreadIdentities(enriched);
      const hasParticipantSignals = identities.size > 0;
      const isParticipant =
        (actorUserId && identities.has(normalizeIdentity(actorUserId))) ||
        (actorEmail && identities.has(actorEmail));

      const scope = hasParticipantSignals && !isParticipant ? 'observed' : 'mine';
      return { ...thread, __threadScope: scope };
    });
  }, [threads, isAdmin, actorUserId, actorEmail, enrichThreadWithBookingContact]);

  const visibleThreads = useMemo(() => {
    if (!isAdmin) return classifiedThreads;
    if (adminThreadFilter === 'mine') {
      return classifiedThreads.filter((thread) => thread.__threadScope === 'mine');
    }
    if (adminThreadFilter === 'observed') {
      return classifiedThreads.filter((thread) => thread.__threadScope === 'observed');
    }
    return classifiedThreads;
  }, [classifiedThreads, isAdmin, adminThreadFilter]);

  const activeThread = useMemo(
    () => classifiedThreads.find((thread) => thread.id === activeThreadId) || null,
    [classifiedThreads, activeThreadId]
  );

  const threadForOwnership = useMemo(
    () => (activeThread ? enrichThreadWithBookingContact(activeThread) : null),
    [activeThread, enrichThreadWithBookingContact]
  );

  const isOrganizerForActiveThread = isActorOrganizerForThread(
    threadForOwnership,
    actorUserId,
    actorEmail
  );

  const isActiveThreadReadOnly = Boolean(
    isAdmin &&
      activeThread &&
      (activeThread.booking_id || activeThread.bookingId) &&
      !isOrganizerForActiveThread
  );

  useEffect(() => {
    if (!urlBookingId || threadBookingContacts[String(urlBookingId)]) return;
    let cancelled = false;
    (async () => {
      try {
        const meta = await userProfileService.getBookingGuestContactMeta(urlBookingId);
        if (!cancelled && meta) {
          setThreadBookingContacts((prev) => ({
            ...prev,
            [String(urlBookingId)]: meta
          }));
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [urlBookingId, threadBookingContacts]);

  useEffect(() => {
    if (!threads.length) return;
    const bookingIds = [
      ...new Set(
        threads
          .map((thread) => thread.booking_id || thread.bookingId)
          .filter(Boolean)
          .map(String)
      )
    ].filter((id) => !threadBookingContacts[id]);
    if (!bookingIds.length) return;

    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        bookingIds.map(async (bookingId) => {
          try {
            const meta = await userProfileService.getBookingGuestContactMeta(bookingId);
            if (!meta) return null;
            return [bookingId, meta];
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      const next = {};
      entries.forEach((entry) => {
        if (entry) next[entry[0]] = entry[1];
      });
      if (Object.keys(next).length > 0) {
        setThreadBookingContacts((prev) => ({ ...prev, ...next }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [threads, threadBookingContacts]);

  useEffect(() => {
    if (!actorUserId) {
      setLoadingThreads(false);
      return;
    }
    const loadThreads = async () => {
      try {
        setLoadingThreads(true);
        setError('');
        const data = await messageThreadsApiService.listThreads({ userId: actorUserId, limit: 50 });
        if (!hasThreadReadMap(actorUserId) && !threadListHasServerUnread(data)) {
          const initialReadMap = buildReadMapFromThreads(data);
          setThreadReadMap(actorUserId, initialReadMap);
          setReadMap(initialReadMap);
        } else {
          setReadMap(getThreadReadMap(actorUserId));
        }
        setThreads(data);
        if (data.length > 0) {
          const matchedThread = preselectedThreadId
            ? data.find((thread) => String(thread.id) === String(preselectedThreadId))
            : null;
          setActiveThreadId((prev) => (matchedThread?.id || prev || data[0].id));
        } else {
          setActiveThreadId(null);
        }
      } catch (err) {
        setError(err.message || 'Failed to load message threads');
      } finally {
        setLoadingThreads(false);
      }
    };
    loadThreads();
  }, [actorUserId, preselectedThreadId]);

  useEffect(() => {
    if (!preselectedThreadId || threads.length === 0) return;
    const matchedThread = threads.find((thread) => String(thread.id) === String(preselectedThreadId));
    if (matchedThread?.id) {
      setActiveThreadId(matchedThread.id);
    }
  }, [preselectedThreadId, threads]);

  useEffect(() => {
    if (visibleThreads.length === 0) {
      setActiveThreadId(null);
      return;
    }
    const isActiveVisible = visibleThreads.some((thread) => thread.id === activeThreadId);
    if (!isActiveVisible) {
      setActiveThreadId(visibleThreads[0].id);
    }
  }, [visibleThreads, activeThreadId]);

  useEffect(() => {
    if (!activeThreadId || !actorUserId) return;
    const active = threads.find((thread) => thread.id === activeThreadId);
    if (!active) return;
    if (!isThreadUnreadForViewer(active, readMap)) return;
    syncThreadReadWithServer(actorUserId, activeThreadId, active.updated_at).then((next) => {
      setReadMap(next);
      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === activeThreadId ? { ...thread, is_unread: false } : thread
        )
      );
    });
  }, [activeThreadId, actorUserId, threads, readMap]);

  const unreadThreadIds = useMemo(() => {
    const ids = new Set();
    threads.forEach((thread) => {
      if (isThreadUnreadForViewer(thread, readMap)) {
        ids.add(thread.id);
      }
    });
    return ids;
  }, [threads, readMap]);

  useEffect(() => {
    if (loadingMessages || !chatBodyRef.current) return;
    chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [messages, loadingMessages, activeThreadId]);

  useEffect(() => {
    if (!activeThreadId || !actorUserId) {
      setMessages([]);
      return;
    }
    const loadMessages = async () => {
      try {
        setLoadingMessages(true);
        setError('');
        const data = await messageThreadsApiService.getThreadMessages(activeThreadId, {
          userId: actorUserId,
          limit: 100,
          messageKind: 'conversation',
        });
        setMessages(data);
      } catch (err) {
        setError(err.message || 'Failed to load messages');
      } finally {
        setLoadingMessages(false);
      }
    };
    loadMessages();
  }, [activeThreadId, actorUserId]);

  const handleSend = async () => {
    if (!composeText.trim() || !activeThreadId || !actorUserId || isActiveThreadReadOnly) return;
    const receiverId = getCounterpartyId(messages, actorUserId);
    try {
      await messageThreadsApiService.sendMessage(activeThreadId, composeText, {
        userId: actorUserId,
        senderId: actorUserId,
        ...(receiverId ? { receiverId } : {}),
      });
      setComposeText('');
        const data = await messageThreadsApiService.getThreadMessages(activeThreadId, {
          userId: actorUserId,
          limit: 100,
          messageKind: 'conversation',
        });
        setMessages(data);
      const active = threads.find((thread) => thread.id === activeThreadId);
      if (active) {
        const next = await syncThreadReadWithServer(
          actorUserId,
          activeThreadId,
          active.updated_at,
          { existingMap: readMap }
        );
        setReadMap(next);
        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === activeThreadId ? { ...thread, is_unread: false } : thread
          )
        );
      }
    } catch (err) {
      setError(err.message || 'Failed to send message');
    }
  };

  const isOwnMessage = (message) =>
    isMessageSentByViewer(message, {
      actorUserId,
      actorEmail,
      viewerContext: 'organizer',
      thread: threadForOwnership,
      venueOwnerId: threadForOwnership?.venue_owner_id || threadForOwnership?.venueOwnerId || null
    });

  return (
    <PageWrapper useNewFooter useLanding2Header>
      <section className="messages-page">
        <div className="messages-layout">
          <aside className="messages-sidebar">
            <h2 className="messages-title">Messages</h2>
            {isAdmin ? (
              <div className="messages-admin-filters">
                <button
                  type="button"
                  className={`messages-admin-filter ${adminThreadFilter === 'all' ? 'messages-admin-filter--active' : ''}`}
                  onClick={() => setAdminThreadFilter('all')}
                >
                  All ({classifiedThreads.length})
                </button>
                <button
                  type="button"
                  className={`messages-admin-filter ${adminThreadFilter === 'mine' ? 'messages-admin-filter--active' : ''}`}
                  onClick={() => setAdminThreadFilter('mine')}
                >
                  My organiser threads ({classifiedThreads.filter((thread) => thread.__threadScope === 'mine').length})
                </button>
                <button
                  type="button"
                  className={`messages-admin-filter ${adminThreadFilter === 'observed' ? 'messages-admin-filter--active' : ''}`}
                  onClick={() => setAdminThreadFilter('observed')}
                >
                  Viewer threads ({classifiedThreads.filter((thread) => thread.__threadScope === 'observed').length})
                </button>
              </div>
            ) : null}
            <div className="messages-thread-list">
              {loadingThreads && <p className="messages-note">Loading threads...</p>}
              {!loadingThreads && visibleThreads.length === 0 && <p className="messages-note">No message threads found.</p>}
              {visibleThreads.map((thread) => (
                <button
                  type="button"
                  key={thread.id}
                  className={`messages-thread ${thread.id === activeThreadId ? 'messages-thread--active' : ''}`}
                  onClick={() => setActiveThreadId(thread.id)}
                >
                  <div className="messages-avatar">{(thread.topic || thread.venue_name || 'T').charAt(0)}</div>
                  <div className="messages-thread-text">
                    <p className="messages-thread-name">{thread.topic || thread.venue_name || 'Untitled thread'}</p>
                    <p className="messages-thread-preview">
                      {thread.venue_name || thread.booking_reference || 'Conversation'}
                    </p>
                  </div>
                  <div className="messages-thread-right">
                    <span className="messages-thread-time">{formatThreadTime(thread.updated_at)}</span>
                    {unreadThreadIds.has(thread.id) ? <span className="messages-unread-pill">1</span> : null}
                  </div>
                </button>
              ))}
            </div>
          </aside>

          <main className="messages-chat">
            <header className="messages-chat-header">
              <div className="messages-chat-user">
                <div className="messages-avatar messages-avatar--large">{(activeThread?.topic || 'T').charAt(0)}</div>
                <h3>{activeThread?.topic || 'Select a thread'}</h3>
              </div>
              <button type="button" className="messages-info-btn" aria-label="Conversation info">
                i
              </button>
            </header>

            <div className="messages-chat-body" ref={chatBodyRef}>
              {loadingMessages && <p className="messages-note">Loading messages...</p>}
              {!loadingMessages && messages.length === 0 && <p className="messages-note">No messages yet.</p>}
              {messages.map((message) => {
                const sentMessage = isOwnMessage(message);
                return (
                  <div
                    key={message.id}
                    className={`messages-bubble-row ${sentMessage ? 'messages-bubble-row--me' : ''}`}
                  >
                    <div className={`messages-bubble ${sentMessage ? 'messages-bubble--me' : ''}`}>
                      <p>{message.body}</p>
                      <span>{formatThreadTime(message.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {isActiveThreadReadOnly ? (
              <p className="messages-note messages-chat-readonly-note">
                Viewer mode: this thread is between another organiser and venue owner.
              </p>
            ) : null}

            <footer className="messages-chat-input">
              <input
                type="text"
                placeholder="Type your message"
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                disabled={!activeThreadId || isActiveThreadReadOnly}
              />
              <button
                type="button"
                aria-label="Send message"
                onClick={handleSend}
                disabled={!activeThreadId || !composeText.trim() || isActiveThreadReadOnly}
              >
                →
              </button>
            </footer>
          </main>
        </div>

        {error && <p className="messages-note">{error}</p>}
        {!currentUser && <p className="messages-note">Sign in to access your real conversations.</p>}
      </section>
    </PageWrapper>
  );
};

export default Messages;
